import {
  ActionReducerMapBuilder,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";
import * as Sentry from "@sentry/react";
import { UserID } from "../interfaces/user.types.ts";
import {
  onChangePassword,
  onGetUserInfo,
  onLogin,
  onLogout,
  onRegister,
} from "../actions/authActions.tsx";
import { OnLoginResponse } from "../interfaces/auth.types.ts";
import Cookies from "js-cookie";
import {
  setUserContext,
  clearUserContext,
  addFlowBreadcrumb,
} from "../../utils/sentryHelpers";

interface AuthState {
  token: string | undefined;
  userInfo: UserID | null;
  error: string | undefined;
  loading: boolean;
}

const initialState = {
  token: Cookies.get("session") || undefined,
  userInfo: null,
  error: undefined,
  loading: false,
} satisfies AuthState as AuthState;

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {},
  extraReducers: (builder: ActionReducerMapBuilder<AuthState>) => {
    builder
      .addCase(onLogin.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(
        onLogin.fulfilled,
        (state, action: PayloadAction<OnLoginResponse>) => {
          state.loading = false;
          state.token = action.payload.token;
          state.error = undefined;

          addFlowBreadcrumb("auth", "User logged in successfully");
        }
      )

      .addCase(onLogin.rejected, (state, action: any) => {
        state.loading = false;
        state.error = action.payload;

        addFlowBreadcrumb("auth", "Login failed");
      });

    builder
      .addCase(onRegister.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(onRegister.fulfilled, (state) => {
        state.loading = false;
        state.error = undefined;

        addFlowBreadcrumb("auth", "User registered successfully");
      })
      .addCase(onRegister.rejected, (state, action: any) => {
        state.loading = false;
        state.error = action.payload;

        addFlowBreadcrumb("auth", "Registration failed");
      });

    builder
      .addCase(onLogout.pending, (state) => {
        state.loading = true;
      })
      .addCase(onLogout.fulfilled, (state) => {
        state.loading = false;
        state.token = undefined;
        state.userInfo = null;

        // Clear all Sentry user context on logout
        clearUserContext();

        addFlowBreadcrumb("auth", "User logged out");
      })
      .addCase(onLogout.rejected, (state) => {
        state.loading = false;
      });

    builder
      .addCase(onGetUserInfo.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(
        onGetUserInfo.fulfilled,
        (state, action: PayloadAction<UserID>) => {
          state.loading = false;
          state.userInfo = action.payload;

          // Set Sentry user context using the helper
          setUserContext({
            id: action.payload.id,
            username: action.payload.name,
            role: action.payload.groups?.[0] ?? undefined,
            isValidated: action.payload.is_validated,
            folderCount: action.payload.folder_count,
          });

          // Add additional context specific to this app
          Sentry.setContext("user_profile_extended", {
            groups: action.payload.groups,
            is_on_leave: action.payload.is_on_leave,
            active_folder_count: action.payload.active_folder_count,
          });

          addFlowBreadcrumb("auth", "User info loaded", {
            user_id: action.payload.id,
            is_validated: action.payload.is_validated,
          });
        }
      )
      .addCase(onGetUserInfo.rejected, (state, action: any) => {
        state.loading = false;
        state.error = action.payload;

        addFlowBreadcrumb("auth", "Failed to load user info");
      });

    builder
      .addCase(onChangePassword.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(onChangePassword.fulfilled, (state) => {
        state.loading = false;

        addFlowBreadcrumb("auth", "Password changed successfully");
      })
      .addCase(onChangePassword.rejected, (state) => {
        state.loading = false;

        addFlowBreadcrumb("auth", "Password change failed");
      });
  },
});

export default authSlice.reducer;
